import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ruutsApi } from '@/services/ruutsApi'
import { errors } from '@/services/ruutsApi/errors'
import { buildPaddocksKml } from '@/utils/Files/buildPaddocksKml'
import { findDuplicateGeometries } from '@/utils/Files/findDuplicateGeometries'
import { getFeaturesFromKML } from '@/utils/Files/importMapsHandle'
import {
  StagedFeature,
  clipStagedFeaturesAgainstExisting,
  findConflictingStagedFeatureIds,
  toStagedFeatures,
} from '@/utils/farmSubdivisions/stagedImportClip'
import { summarizePartialImport } from '@/utils/farmSubdivisions/partialImportSummary'
import { stripUnsupportedPaddockNameChars } from '@/utils/farmSubdivisions/normalizePaddockName'

// Lives here rather than src/hooks/data/ because it orchestrates a staging workflow plus a
// mutation (KML upload + business-outcome handling), not a data-fetching query.

// Staging is local-only by design: parsed lotes are reviewed and edited (rename/remove/clip) with
// zero server writes, and only an explicit confirm serializes them into a KML and uploads it. The
// staged features are owned by AllotmentHome so the map can render them as a preview layer.
export type PartialImportOutcome = {
  skippedNames: string[]
}

// FileReader (not the newer Blob.text()) is used deliberately for jsdom/Testing Library
// compatibility — jsdom implements FileReader natively but not File.prototype.text().
const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })

// The client parse is authoritative for staging, so it must yield every polygon the operator
// expects to see. The parser buckets features by placemark-name keywords, not by KML folder like
// the backend does — so polygons whose names lack a lote-ish keyword land in "unassigned" and must
// be staged too, or they would silently vanish from the import.
const parseKmlPolygonFeatures = async (file: File): Promise<GeoJSON.Feature[]> => {
  const data = await readFileAsText(file)
  const features: GeoJSON.Feature[] = []
  getFeaturesFromKML({
    data,
    handlePaddocks: parsed => features.push(...parsed),
    handleUnassigned: parsed => features.push(...parsed),
  })
  return features
}

export const usePartialImportKml = ({
  farmSubdivisionId,
  existingFeatures,
  stagedFeatures,
  setStagedFeatures,
}: {
  farmSubdivisionId: string | undefined
  existingFeatures: GeoJSON.Feature[]
  stagedFeatures: StagedFeature[]
  setStagedFeatures: Dispatch<SetStateAction<StagedFeature[]>>
}) => {
  const [isImporting, setIsImporting] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Staged features belong to the loteo they were imported for; drop them when the hook is
  // retargeted so a confirm can never upload a stale staging to a different subdivision. Also
  // abort any confirm still in flight for the PREVIOUS subdivision.
  useEffect(() => {
    abortControllerRef.current?.abort()
    setStagedFeatures([])
  }, [farmSubdivisionId, setStagedFeatures])

  // Cancel an in-flight confirm when the panel unmounts (e.g. edit mode is left mid-upload),
  // instead of letting the request finish only to update state on an unmounted hook.
  useEffect(() => () => abortControllerRef.current?.abort(), [])

  const stageKmlFile = useCallback(
    async (file: File): Promise<{ stagedCount: number }> => {
      const staged = toStagedFeatures(await parseKmlPolygonFeatures(file))
      if (!staged.length) {
        // Internal guard, not user-facing copy — the caller (AllotmentPanel) replaces any thrown
        // message with a generic Spanish one before display.
        throw new errors.BusinessEntityError({
          message: 'No paddock polygons found in the KML file',
          needSupport: false,
        })
      }
      setStagedFeatures(staged)
      return { stagedCount: staged.length }
    },
    [setStagedFeatures],
  )

  // Characters the backend would strip are dropped as they are typed: a name the operator can see
  // but never obtain is worse than a keystroke that visibly does nothing. Whitespace is left alone
  // so a name with spaces remains typable — the upload trims it.
  const renameStagedFeature = useCallback(
    (stagedId: string, name: string) => {
      setStagedFeatures(prev =>
        prev.map(feature =>
          feature.properties.stagedId === stagedId
            ? {
                ...feature,
                properties: {
                  ...feature.properties,
                  name: stripUnsupportedPaddockNameChars(name),
                },
              }
            : feature,
        ),
      )
    },
    [setStagedFeatures],
  )

  const removeStagedFeature = useCallback(
    (stagedId: string) => {
      setStagedFeatures(prev => prev.filter(feature => feature.properties.stagedId !== stagedId))
    },
    [setStagedFeatures],
  )

  // The override exists for conflict recovery: the backend re-classifies overlaps against the
  // paddocks present when its transaction runs, so a batch clipped clean against the panel's
  // snapshot can still be rejected. Re-clipping against that same snapshot yields byte-identical
  // geometry and the identical rejection, so the caller must be able to hand over paddocks it has
  // just re-read from the API — before they have propagated back into existingFeatures.
  const clipStagedFeatures = useCallback(
    (existingFeaturesOverride?: GeoJSON.Feature[]): { removedNames: string[] } => {
      const { clippedFeatures, removedNames } = clipStagedFeaturesAgainstExisting({
        stagedFeatures,
        existingFeatures: existingFeaturesOverride ?? existingFeatures,
      })
      setStagedFeatures(clippedFeatures)
      return { removedNames }
    },
    [stagedFeatures, existingFeatures, setStagedFeatures],
  )

  const discardStagedImport = useCallback(() => {
    setStagedFeatures([])
  }, [setStagedFeatures])

  // Manual render-time memo keyed on GEOMETRY identity, not array identity: detection is an
  // n x m turf-intersect sweep, and a rename replaces the staged array (and a paddock edit the
  // existing one) with every geometry reference intact — useMemo on the arrays would re-sweep
  // the whole working set on each keystroke. useMemo cannot express this key: its deps are a
  // fixed-length identity list, and what must be compared here is a VARIABLE number of geometry
  // references, element by element. Do not "simplify" this into useMemo([stagedFeatures,
  // existingFeatures]) — that is the re-sweep this exists to avoid. The write is idempotent and
  // recomputed whenever the key differs, so a StrictMode double render or a discarded concurrent
  // render cannot serve a stale value. Ids are cached instead of names so the conflict warning
  // still shows the current name after a rename.
  const conflictDetectionCacheRef = useRef<{
    stagedGeometries: GeoJSON.Geometry[]
    existingGeometries: GeoJSON.Geometry[]
    conflictingIds: string[]
    duplicateIds: string[]
  }>({ stagedGeometries: [], existingGeometries: [], conflictingIds: [], duplicateIds: [] })

  const stagedGeometries = stagedFeatures.map(feature => feature.geometry)
  const existingGeometries = existingFeatures.map(feature => feature.geometry)
  const sameGeometries = (left: GeoJSON.Geometry[], right: GeoJSON.Geometry[]) =>
    left.length === right.length && left.every((geometry, index) => geometry === right[index])
  const cache = conflictDetectionCacheRef.current
  if (
    !sameGeometries(stagedGeometries, cache.stagedGeometries) ||
    !sameGeometries(existingGeometries, cache.existingGeometries)
  ) {
    conflictDetectionCacheRef.current = {
      stagedGeometries,
      existingGeometries,
      conflictingIds: stagedFeatures.length
        ? findConflictingStagedFeatureIds({ stagedFeatures, existingFeatures })
        : [],
      // Ids, not names: a rename must not clear or move the duplicate flag, and it leaves every
      // geometry reference intact so this block does not re-run.
      duplicateIds: findDuplicateGeometries(stagedFeatures)
        .flat()
        .map(index => stagedFeatures[index].properties.stagedId),
    }
  }
  const { conflictingIds, duplicateIds } = conflictDetectionCacheRef.current

  const conflictingFeatureNames = useMemo(() => {
    const conflictingIdSet = new Set(conflictingIds)
    return stagedFeatures
      .filter(feature => conflictingIdSet.has(feature.properties.stagedId))
      .map(feature => feature.properties.name)
  }, [conflictingIds, stagedFeatures])

  const confirmImport = useCallback(async (): Promise<PartialImportOutcome | null> => {
    if (!farmSubdivisionId) {
      throw new Error('usePartialImportKml: no farmSubdivisionId to import into')
    }
    if (!stagedFeatures.length) return null
    setIsImporting(true)
    const controller = new AbortController()
    abortControllerRef.current = controller
    try {
      // Names are trimmed at upload time (renames keep raw text so spaces can be typed): the
      // serialized KML and the skip diff must agree with the trim-based validation, or a lote
      // persisted with a trimmed name would be misreported as an auto-skipped duplicate.
      const featuresToUpload = stagedFeatures.map(feature => ({
        ...feature,
        properties: { ...feature.properties, name: feature.properties.name.trim() },
      }))
      // clipFeatureNames stays empty by design: clipping is client-side now, so the geometries
      // uploaded are exactly the ones the operator reviewed — the backend must never re-clip.
      const file = new File([buildPaddocksKml(featuresToUpload)], 'lotes-importados.kml', {
        type: 'application/vnd.google-earth.kml+xml',
      })
      const { paddocksCreated } = await ruutsApi.v2.farmSubdivisions.partialImport({
        farmSubdivisionId,
        file,
        clipFeatureNames: [],
        signal: controller.signal,
      })
      const { skippedNames } = summarizePartialImport({
        importedFeatureNames: featuresToUpload.map(feature => feature.properties.name),
        paddocksCreated,
      })
      setStagedFeatures([])
      return { skippedNames }
    } catch (error) {
      // A deliberate abort (unmount/retarget) is not a failed import — the backend's write is
      // atomic and may have already committed by the time the request is cancelled client-side,
      // so rejecting here would tell the operator "import failed" for a write that could well
      // have succeeded. Degrade to a silent no-op instead.
      if (controller.signal.aborted) return null
      // Any other failure keeps the staging intact so the operator can clip, edit or discard.
      throw error
    } finally {
      setIsImporting(false)
      if (abortControllerRef.current === controller) abortControllerRef.current = null
    }
  }, [farmSubdivisionId, stagedFeatures, setStagedFeatures])

  return {
    stageKmlFile,
    renameStagedFeature,
    removeStagedFeature,
    clipStagedFeatures,
    confirmImport,
    discardStagedImport,
    isImporting,
    conflictingFeatureNames,
    hasConflict: conflictingFeatureNames.length > 0,
    duplicateStagedIds: duplicateIds,
    hasDuplicateGeometries: duplicateIds.length > 0,
  }
}
